/**
 * App Bootstrap
 *
 * Single awaited startup routine that hydrates the store from the aggregated
 * user-context endpoint before the app renders. Replaces the previous trio of
 * fire-and-forget calls (`/auth/me` + `/api/v1/workspaces` + settings).
 *
 * The promise is memoized so concurrent callers (e.g. ProtectedRoute and
 * useSettingsSync, plus React StrictMode double-invocation) share one request.
 */

import { contextService } from '@/services/api';
import { isApiError } from '@/utils/apiErrorMessage';
import { useGeoMarkupStore } from './index';
import { applySettings } from './settingsBridge';

let bootstrapPromise: Promise<void> | null = null;

/** True when the error represents a confirmed unauthenticated (401) response. */
function isUnauthorizedError(error: unknown): boolean {
  return isApiError(error) && error.code === 'UNAUTHORIZED';
}

async function runBootstrap(): Promise<void> {
  try {
    const response = await contextService.getUserContext();
    const context = response.data;

    const state = useGeoMarkupStore.getState();
    state.auth.applyBootstrapContext(context);
    state.workspace.setExtendedWorkspaces(context.workspaces);
    applySettings(
      context.settings,
      useGeoMarkupStore.setState.bind(useGeoMarkupStore)
    );
  } catch (error) {
    if (isUnauthorizedError(error)) {
      // Confirmed unauthenticated (401): ProtectedRoute redirects to OAuth provider.
      useGeoMarkupStore.setState((state) => {
        state.auth.isAuthenticated = false;
        state.auth.isCheckingAuth = false;
      });
      return;
    }
    // Transient failure (network, 503, etc.): authentication state is unknown.
    // Reset the memoized promise so a retry attempt can re-run bootstrap.
    console.error('App bootstrap failed:', error);
    resetAppBootstrap();
    throw error;
  }
}

/**
 * Ensures the app has been bootstrapped, returning the shared (memoized)
 * bootstrap promise. Safe to await from multiple call sites.
 */
export function ensureAppBootstrap(): Promise<void> {
  return (bootstrapPromise ??= runBootstrap());
}

/**
 * Clears the memoized bootstrap so the next `ensureAppBootstrap` re-fetches.
 * Call on logout / session reset.
 */
export function resetAppBootstrap(): void {
  bootstrapPromise = null;
}
